import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, MessageCircle } from "lucide-react";
import { useShop } from "../context/ShopContext";
import { formatPrice } from "../data/products";
import { WHATSAPP_NUMBER } from "../i18n";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

const CartDrawer = () => {
  const { lang, t, cart, cartOpen, setCartOpen, updateQty, removeFromCart, clearCart, products, productMedia, contacts } = useShop();
  const navigate = useNavigate();

  const waNumber = (contacts || []).find((c) => c.type === "whatsapp")?.value || WHATSAPP_NUMBER;

  const items = useMemo(
    () =>
      cart
        .map((item) => {
          const product = (products || []).find((p) => p.id === item.id);
          if (!product) return null;
          const dims = item.dims || product.available_dims?.[0] || product.dims;
          return { ...item, dims, product };
        })
        .filter(Boolean),
    [cart, products]
  );

  const total = items.reduce((sum, i) => sum + i.product.price * i.qty, 0);

  const whatsappUrl = useMemo(() => {
    const lines = items.map(
      (i) =>
        `- ${i.product.name[lang]} (${i.dims}) x${i.qty} — ${formatPrice(i.product.price * i.qty)}`
    );
    const msg = `${t.whatsapp.cartMsg}\n${lines.join("\n")}\n${t.whatsapp.total}: ${formatPrice(total)}`;
    return `https://wa.me/${waNumber}?text=${encodeURIComponent(msg)}`;
  }, [items, lang, t, total, waNumber]);

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-cream/95 backdrop-blur-xl border-l border-brown/10 text-brown flex flex-col p-0"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-8 pt-8 pb-6 border-b border-brown/10">
          <SheetTitle className="font-serif text-2xl font-medium text-brown text-left">
            {t.cart.title}
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-6 px-8" data-testid="cart-empty-state">
            <p className="text-brown-light font-light">{t.cart.empty}</p>
            <button
              onClick={() => {
                setCartOpen(false);
                navigate("/urunler");
              }}
              className="px-6 py-3 rounded-full bg-olive text-cream text-sm font-medium hover:bg-olive-dark transition-colors duration-300"
              data-testid="cart-browse-button"
            >
              {t.cart.emptyCta}
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-8 py-6 flex flex-col gap-8">
              {items.map(({ product, qty, dims }) => (
                <div key={`${product.id}-${dims}`} className="flex gap-5" data-testid={`cart-item-${product.id}-${dims.replace(/[^0-9]/g, "-")}`}>
                  <div className="w-20 h-24 shrink-0 overflow-hidden bg-cream-dark">
                    <img
                      src={productMedia(product)}
                      alt={product.name[lang]}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h4 className="font-serif text-lg font-medium leading-tight">
                          {product.name[lang]}
                        </h4>
                        <span className="inline-block mt-1.5 px-3 py-0.5 rounded-full border border-brown/20 text-[11px] tracking-wide text-brown-light">
                          {dims}
                        </span>
                      </div>
                      <button
                        onClick={() => removeFromCart(product.id, dims)}
                        className="text-brown-muted hover:text-brown transition-colors duration-200 p-1"
                        aria-label="Remove"
                        data-testid={`cart-remove-${product.id}-${dims.replace(/[^0-9]/g, "-")}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                    <div className="mt-4 flex items-center justify-between">
                      <div className="flex items-center border border-brown/15 rounded-full">
                        <button
                          onClick={() => updateQty(product.id, dims, -1)}
                          className="w-8 h-8 flex items-center justify-center text-brown-light hover:text-brown transition-colors"
                          aria-label="Decrease"
                          data-testid={`cart-decrease-${product.id}-${dims.replace(/[^0-9]/g, "-")}`}
                        >
                          <Minus size={13} />
                        </button>
                        <span className="w-6 text-center text-sm font-medium" data-testid={`cart-qty-${product.id}-${dims.replace(/[^0-9]/g, "-")}`}>
                          {qty}
                        </span>
                        <button
                          onClick={() => updateQty(product.id, dims, 1)}
                          className="w-8 h-8 flex items-center justify-center text-brown-light hover:text-brown transition-colors"
                          aria-label="Increase"
                          data-testid={`cart-increase-${product.id}-${dims.replace(/[^0-9]/g, "-")}`}
                        >
                          <Plus size={13} />
                        </button>
                      </div>
                      <p className="text-sm font-medium">{formatPrice(product.price * qty)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-brown/10 px-8 py-6">
              <div className="flex items-center justify-between mb-5">
                <span className="text-sm tracking-wide text-brown-light">{t.cart.total}</span>
                <span className="font-serif text-2xl font-medium" data-testid="cart-total">
                  {formatPrice(total)}
                </span>
              </div>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-[#4F5D3A] text-cream text-sm font-medium tracking-wide hover:bg-olive-dark transition-colors duration-300"
                data-testid="cart-whatsapp-order-button"
              >
                <MessageCircle size={17} />
                {t.cart.orderWhatsapp}
              </a>
              <p className="mt-4 text-xs font-light text-brown-muted text-center">{t.cart.note}</p>
              <button
                onClick={clearCart}
                className="mt-3 w-full text-xs tracking-widest uppercase text-brown-muted hover:text-brown transition-colors duration-200"
                data-testid="cart-clear-button"
              >
                {t.cart.clear}
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
